import React, { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import {
  toDateFromHoursMinutesSeconds,
  toIntlDateTimeFormat,
  zoneToUtcOffset,
} from '@zedoc/date';
import { Markdown } from '@zedoc/react-markdown';
import Table from '../../../components/lists/Table';
import { openEditNoticeDialog } from '../store';
import { getIcon } from '../../../components/NoticeBoard/helpers';
import { apiAdminAllNoticeBoards } from '../../../common/api/admin';
import { default as NoticeBoardSelect } from '../../../common/selectors/NoticeBoard';
import { selectQueryParam } from '../../../store/router';

const isNoticeExpired = (notice) => {
  const now = Date.now();
  const dateTimeStr = `${notice.dateEnd}T${notice.timeEnd || '00:00:00'}`;
  const localDateTime = new Date(dateTimeStr);

  let endTimeUTC;
  if (notice.timezone) {
    const getUtcOffset = zoneToUtcOffset(notice.timezone);
    const offsetMinutes = getUtcOffset(localDateTime);
    endTimeUTC = localDateTime.getTime() - offsetMinutes * 60000;
  } else {
    endTimeUTC = localDateTime.getTime();
  }

  return now > endTimeUTC;
};

const NoticeBoardTable = ({ canUpdate, canDelete }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { ready: subscriptionsReady } = useDDPSubscription(
    apiAdminAllNoticeBoards.withParams(),
  );
  const allNotices = useSelector(NoticeBoardSelect.all()) || [];
  const searchQuery = useSelector(selectQueryParam('search', ''));
  const notices = useMemo(() => {
    let filteredNotices = allNotices;

    if (searchQuery && searchQuery.trim()) {
      const searchTerm = searchQuery.trim().toLowerCase();

      filteredNotices = allNotices.filter((notice) => {
        if (notice.author && notice.author.toLowerCase().includes(searchTerm)) {
          return true;
        }

        if (notice.subject) {
          if (
            notice.subject[language] &&
            notice.subject[language].toLowerCase().includes(searchTerm)
          ) {
            return true;
          }
          if (
            language !== 'en' &&
            notice.subject.en &&
            notice.subject.en.toLowerCase().includes(searchTerm)
          ) {
            return true;
          }
          const allSubjectValues = Object.values(notice.subject);
          if (
            allSubjectValues.some(
              (text) => text && text.toLowerCase().includes(searchTerm),
            )
          ) {
            return true;
          }
        }

        if (notice.content) {
          if (
            notice.content[language] &&
            notice.content[language].toLowerCase().includes(searchTerm)
          ) {
            return true;
          }
          if (
            language !== 'en' &&
            notice.content.en &&
            notice.content.en.toLowerCase().includes(searchTerm)
          ) {
            return true;
          }
          const allContentValues = Object.values(notice.content);
          if (
            allContentValues.some(
              (text) => text && text.toLowerCase().includes(searchTerm),
            )
          ) {
            return true;
          }
        }

        return false;
      });
    }

    return filteredNotices.sort((a, b) => {
      const aIsExpired = isNoticeExpired(a);
      const bIsExpired = isNoticeExpired(b);

      if (aIsExpired !== bIsExpired) {
        return aIsExpired ? 1 : -1;
      }

      const getStartTimeUTC = (notice) => {
        const dateTimeStr = `${notice.dateStart}T${
          notice.timeStart || '00:00:00'
        }`;
        const localDateTime = new Date(dateTimeStr);

        if (notice.timezone) {
          const getUtcOffset = zoneToUtcOffset(notice.timezone);
          const offsetMinutes = getUtcOffset(localDateTime);
          return localDateTime.getTime() - offsetMinutes * 60000;
        }
        return localDateTime.getTime();
      };

      return getStartTimeUTC(a) - getStartTimeUTC(b);
    });
  }, [allNotices, searchQuery, language]);

  const dispatch = useDispatch();
  const handleSelectNotice = useCallback(
    (notice) => {
      dispatch(openEditNoticeDialog(notice._id));
    },
    [dispatch],
  );

  const columns = useMemo(
    () => [
      {
        title: t('notices', { count: 1 }),
        key: 'notice',
        render: (_, notice) => {
          const isExpired = isNoticeExpired(notice);

          return (
            <div className={`stack-1 ${isExpired ? 'opacity-50' : ''}`}>
              <div className="cluster-2 items-center">
                {getIcon(notice.type)}
                <span className="font-medium">
                  {notice.subject[language] || Object.values(notice.subject)[0]}
                </span>
              </div>
              <Markdown
                source={
                  notice.content[language] || Object.values(notice.content)[0]
                }
              />
              <span className="text-sm">By {notice.author}</span>
            </div>
          );
        },
      },
      {
        title: t('forms:dateStart.label'),
        key: 'dateStart',
        render: (_, notice) => {
          const isExpired = isNoticeExpired(notice);

          return (
            <span
              className={`stack-0 text-sm ${isExpired ? 'opacity-50' : ''}`}
            >
              <span>
                {toIntlDateTimeFormat(language, new Date(notice.dateStart))}
              </span>
              <span>
                {toIntlDateTimeFormat(
                  language,
                  toDateFromHoursMinutesSeconds(notice.timeStart),
                  {
                    timeStyle: 'medium',
                  },
                )}
              </span>
            </span>
          );
        },
      },
      {
        title: t('forms:dateEnd.label'),
        key: 'dateEnd',
        render: (_, notice) => {
          const isExpired = isNoticeExpired(notice);

          return (
            <span
              className={`stack-0 text-sm ${isExpired ? 'opacity-50' : ''}`}
            >
              <span>
                {toIntlDateTimeFormat(language, new Date(notice.dateEnd))}
              </span>
              <span>
                {toIntlDateTimeFormat(
                  language,
                  toDateFromHoursMinutesSeconds(notice.timeEnd),
                  {
                    timeStyle: 'medium',
                  },
                )}
              </span>
            </span>
          );
        },
      },
      {
        title: t('forms:timezone.label'),
        key: 'timezone',
        render: (timezone, notice) => {
          const isExpired = isNoticeExpired(notice);

          return (
            <span className={`text-sm ${isExpired ? 'opacity-50' : ''}`}>
              {timezone}
            </span>
          );
        },
      },
    ],
    [t, language],
  );

  return (
    <Table
      dataSource={notices}
      columns={columns}
      loading={!subscriptionsReady}
      onRow={(notice) => ({
        onClick: () =>
          canUpdate || canDelete ? handleSelectNotice(notice) : null,
      })}
      // pagination={getPaginationProps()}
    />
  );
};

export default NoticeBoardTable;
